'use client';

import classNames from 'classnames';
import { useIntl } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import Link from '@alltrails/denali/components/Link';
import type Trail from '@alltrails/types/Trail';
import type TrailDetails from '@alltrails/types/TrailDetails';
import { getUrlFromSlug } from '../../utils/cardLinks';
import TrailMetadataStats from '../TrailMetadataStats/TrailMetadataStats';
import styles from './styles/styles.module.scss';

type TrailMetadataProps = {
  trail: Trail | TrailDetails;
  openInNewTab?: boolean;
  baseUrl: string;
  displayRank?: number;
  variant?: 'small' | 'standard' | 'feed';
};

const TrailMetadata = ({ trail, openInNewTab, baseUrl, displayRank, variant }: TrailMetadataProps) => {
  const {
    area,
    defaultActivityStats: { difficulty },
    avgRating,
    name,
    trailGeoStats: { length: meters, durationMinutes, averageTimeToComplete }
  } = trail;
  const intl = useIntl();
  const locationName = area?.name;
  const locationSlug = area?.slug;
  const testIdPrefix = `TrailCard_${name}`;

  const locationElement: React.ReactNode = locationSlug ? (
    <Link
      className={styles.link}
      size="sm"
      variant="secondary"
      href={getUrlFromSlug(locationSlug, baseUrl)}
      noUnderline
      testId={`${testIdPrefix}_Location_Link`}
      target={openInNewTab ? '_blank' : undefined}
    >
      {locationName}
    </Link>
  ) : (
    locationName
  );

  const displayName = displayRank ? `#${displayRank} - ${name}` : name;

  if (variant === 'small') {
    return (
      <div>
        <Typography variant="text200bold" className={styles.truncating} component="div">
          {displayName}
        </Typography>
        <Typography variant="text100" className={classNames(styles.location, styles.truncating)} component="div">
          {locationElement}
        </Typography>
        <TrailMetadataStats avgRating={avgRating} compact difficulty={difficulty} size="sm" />
        <TrailMetadataStats
          durationText={
            averageTimeToComplete
              ? `${intl.formatMessage({ defaultMessage: 'Est.' })} ${averageTimeToComplete.value} ${averageTimeToComplete.unit}`
              : undefined
          }
          durationMinutes={durationMinutes !== undefined ? Number(durationMinutes) : undefined}
          isDurationMinutesEstimate
          lengthMeters={meters}
          size="sm"
        />
      </div>
    );
  }

  return (
    <div>
      <Typography variant="text200bold" className={styles.truncating} component="div">
        {displayName}
      </Typography>
      <Typography variant="text100" className={classNames(styles.location, styles.truncating)} component="div">
        {locationElement}
      </Typography>
      <TrailMetadataStats
        avgRating={avgRating}
        compact
        difficulty={difficulty}
        durationText={
          averageTimeToComplete
            ? `${intl.formatMessage({ defaultMessage: 'Est.' })} ${averageTimeToComplete.value} ${averageTimeToComplete.unit}`
            : undefined
        }
        durationMinutes={durationMinutes !== undefined ? Number(durationMinutes) : undefined}
        isDurationMinutesEstimate
        lengthMeters={meters}
        size="sm"
      />
    </div>
  );
};

export default TrailMetadata;
